import { Button } from "@mui/material";
import { useEffect, useState, useContext, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import { Customer } from "../models/customer";
import { customersApi } from "../services/api/customers";
import { ordersApi } from "../services/api/orders";
import OrderForm, {
  OrderFormValues,
  OrderRecurType,
} from "../components/order-form";
import { productsApi } from "../services/api/products";
import { Product } from "../models/product";
import { UserContext, ErrorContext } from "../components/app-routes";
import { formatCurrency, toCurrency } from "./currency";
import { v4 } from "uuid";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import { Order } from "../models/order";
import type { FormikHelpers } from "formik";
import StickyActionBar from "../components/sticky-action-bar";
import { getCustomLabel } from "../utils/custom-labels";

interface Data {
  customers: Customer[];
  products: Product[];
}

export default function AddOrderPage() {
  const history = useHistory();
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasSaved, setWasSaved] = useState(false);
  const { grower } = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const { state } = useLocation<{ order: Order }>();
  const isMounted = useRef(true);

  if (!grower) {
    throw new Error("Invalid UserContext");
  }

  const formInitialValues: OrderFormValues = state
    ? {
        customerId: state.order.customerId.toString(),
        dateType: "harvestDate",
        date: null,
        recur: {
          id: "",
          type: OrderRecurType.None,
          endDate: null,
        },
        orderProducts: state.order.orderProducts.map((v) => ({
          id: v4(),
          productId: v.productId.toString(),
          productSizeId: v.productSizeId.toString(),
          quantity: v.quantity.toString(),
          price: formatCurrency(v.price, grower.CountryISO, {
            symbol: "",
            separator: "",
          }),
        })),
        notes: state.order.notes,
        deliveryDateOffset: "0",
      }
    : {
        customerId: "",
        dateType: "harvestDate",
        date: null,
        recur: {
          id: "",
          type: OrderRecurType.None,
          endDate: null,
        },
        orderProducts: [
          {
            id: v4(),
            productId: "",
            productSizeId: "",
            quantity: "",
            price: "",
          },
        ],
        notes: "",
        deliveryDateOffset: "0",
      };

  useEffect(() => {
    isMounted.current = true;

    const load = async () => {
      if (isMounted.current) setBusy(true);
      try {
        const [customers, products] = await Promise.all([
          customersApi.getAll(),
          productsApi.getAll(),
        ]);

        if (isMounted.current) {
          setData({ customers, products });
        }
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    };

    load();

    return () => {
      isMounted.current = false;
    };
  }, [handleError]);

  useEffect(() => {
    if (wasSaved) {
      history.replace("/home/orders");
    }
  }, [wasSaved]);

  const handleSubmit = async (
    values: OrderFormValues,
    _helpers: FormikHelpers<OrderFormValues>,
  ) => {
    if (!values.date) {
      throw new Error("Invalid state");
    }
    setBusy(true);

    try {
      let harvestDate: string | null;

      if (values.dateType === "startDate") {
        const products = await productsApi.getAll();

        const daysToHarvest = Math.max(
          ...values.orderProducts.flatMap((oPs) => {
            const product = products.find(
              (v) => v.id === parseInt(oPs.productId),
            );

            if (!product) {
              throw new Error("Invalid state");
            }

            return product.productCrops.map((v) => v.crop.daysToMaturity);
          }),
        );

        harvestDate = values.date.plus({ day: daysToHarvest }).toISODate();
      } else {
        harvestDate = values.date.toISODate();
      }

      if (!harvestDate) {
        throw new Error("Invalid state");
      }

      const deliveryDateOffset = parseInt(values.deliveryDateOffset);

      await ordersApi.add({
        id: 0,
        customerId: parseInt(values.customerId),
        harvestDate,
        recur:
          values.recur?.type !== OrderRecurType.None && values.recur?.endDate
            ? {
                id: null as unknown as number,
                type: values.recur.type,
                endDate: values.recur.endDate.toISODate()!,
              }
            : undefined,
        orderProducts: values.orderProducts.map((oP) => ({
          productId: parseInt(oP.productId),
          productSizeId: parseInt(oP.productSizeId),
          quantity: parseInt(oP.quantity),
          price: toCurrency(oP.price, grower.CountryISO).intValue,
        })),
        customer: null as unknown as Customer,
        notes: values.notes,
        deliveryDate:
          deliveryDateOffset > 0
            ? values.date.plus({ days: deliveryDateOffset }).toISODate()
            : null,
      });

      setWasSaved(true);

      mixpanel.track("Add Order");

      if (isMounted.current) {
        history.replace("/home/orders");
      }
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) {
        setBusy(false);
      }
    }
  };

  const handleOnClickCancel = () => {
    goBackOrTo(history, "/home/orders");
  };

  const context = useContext(UserContext);

  return (
    <Page title={`Add ${getCustomLabel(grower ?? null, "orders")}`}>
      {" "}
      {data && formInitialValues && (
        <OrderForm
          formId="order-form"
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          customers={data.customers}
          products={data.products}
          saved={wasSaved}
        >
          <StickyActionBar position="bottom">
            <Button
              variant="outlined"
              disabled={busy}
              onClick={handleOnClickCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="order-form"
              disabled={busy}
            >
              Add
            </Button>
          </StickyActionBar>
        </OrderForm>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
